import { type ComponentProps, type ReactNode } from 'react';

import { RadioGroup } from '@eneco/sparky';
import {
  Control,
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  PathValue,
} from 'react-hook-form';

type RadioGroupProps = ComponentProps<typeof RadioGroup>;
type RadioGroupNameProps = { label: string } | { 'aria-labelledby': string };

type Props<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  control: Control<TFieldValues>;
  name: TName;
  error?: string;
  children: ReactNode;
  rules?: ControllerProps<TFieldValues, TName>['rules'];
} & Omit<RadioGroupProps, 'name' | 'value' | 'onValueChange' | 'error' | 'children' | 'defaultValue'>;

const toRadioGroupNameProps = (label?: string, ariaLabelledby?: string): RadioGroupNameProps =>
  label ? { label } : { 'aria-labelledby': ariaLabelledby ?? '' };

/**
 * Radio group wired to react-hook-form for flow steps.
 * Forwards `ref` so failed submit can focus the group, and normalises the value
 * to a string so an unanswered choice stays empty (and required validation fires).
 */
export const FlowRadioGroup = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  control,
  name,
  error,
  children,
  rules,
  label,
  'aria-labelledby': ariaLabelledby,
  ...radioGroupProps
}: Props<TFieldValues, TName>) => (
  <Controller
    control={control}
    name={name}
    rules={rules}
    render={({ field: { onChange, value, name: fieldName, ref } }) => (
      <RadioGroup
        {...radioGroupProps}
        {...toRadioGroupNameProps(label, ariaLabelledby)}
        ref={ref}
        name={fieldName}
        value={value == null ? '' : String(value)}
        onValueChange={next => onChange(next as PathValue<TFieldValues, TName>)}
        error={error}>
        {children}
      </RadioGroup>
    )}
  />
);
